import type { IBiomaterial } from '@zero-dash/types';
import {
  CHART_LANE_LABEL,
  CHART_LANE_LOG,
  CHART_LANE_Y_PADDING_PCT,
  TIMELINE_VIEW_MARGIN_PCT,
  UNKNOWN_ANALYTE,
  UNKNOWN_ASSAY,
  UNKNOWN_MANIFEST_ID,
} from '@/constants/PatientDashboard/patientTimeline';
import type { IBiosample } from '@/types/Analysis/Biosamples.types';
import type {
  IChartLabel,
  IChartLaneLogScale,
  IChartLanePointFields,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { getValidDate } from './PatientTimeline.util';

const CHART_VALUE_DECIMALS = 2;
const SIGNIFICANT_DIGITS_BELOW_ONE = 2;
const MAX_FRACTION_DIGITS = 100;

/**
 * Drops the padding zeros a `toFixed` result carries, and the decimal point with them once
 * nothing follows it: `'1.50'` → `'1.5'`, `'2.00'` → `'2'`. `0+` cannot match across the `.`,
 * so the zeros of a whole number are safe — `'100.00'` trims to `'100'`, never `'1'`.
 */
const trimTrailingZeros = (fixed: string): string => fixed.replace(/\.?0+$/, '');

export const formatChartValue = (value: number): string => {
  if (value <= 0) {
    return '0%';
  }
  if (value >= 1) {
    return `${trimTrailingZeros(value.toFixed(CHART_VALUE_DECIMALS))}%`;
  }
  const decimals = Math.min(
    MAX_FRACTION_DIGITS,
    SIGNIFICANT_DIGITS_BELOW_ONE - 1 - Math.floor(Math.log10(value)),
  );
  return `${trimTrailingZeros(value.toFixed(decimals))}%`;
};

const LOG_SCALE_PRECISION = 12;
const DECADE = 10;

const decadeOf = (value: number): number => DECADE ** Math.floor(Math.log10(value));

const LANE_MIDPOINT_PCT = 50;
const PLOT_HEIGHT_PCT = 100 - 2 * CHART_LANE_Y_PADDING_PCT;

const snapAboveValue = (value: number): number => {
  const decade = decadeOf(value);
  const mantissa = value / decade;
  const step = Math.min(...CHART_LANE_LOG.ladder.filter((candidate) => candidate > mantissa));
  return Number((decade * step).toPrecision(LOG_SCALE_PRECISION));
};

const isClearOfRails = (tick: number, min: number, max: number): boolean => {
  const span = Math.log10(max) - Math.log10(min);
  const belowTop = ((Math.log10(max) - Math.log10(tick)) / span) * PLOT_HEIGHT_PCT;
  const aboveBottom = ((Math.log10(tick) - Math.log10(min)) / span) * PLOT_HEIGHT_PCT;
  return Math.min(belowTop, aboveBottom) >= CHART_LANE_LOG.minTickGapPct;
};

const buildLogTicks = (min: number, max: number): number[] => {
  const ticks = [max];
  for (let exponent = Math.floor(Math.log10(max)); DECADE ** exponent > min; exponent -= 1) {
    const tick = DECADE ** exponent;
    if (tick < max && isClearOfRails(tick, min, max)) {
      ticks.push(tick);
    }
  }
  return [...ticks, min];
};

export const buildChartLaneLogScale = (values: number[]): IChartLaneLogScale => {
  const plottable = values.filter((value) => value > 0);
  const max = plottable.length
    ? Math.min(CHART_LANE_LOG.maxPct, snapAboveValue(Math.max(...plottable)))
    : CHART_LANE_LOG.maxPct;
  const shortestSpanFloor = Number(
    (max / DECADE ** CHART_LANE_LOG.minDecades).toPrecision(LOG_SCALE_PRECISION),
  );
  const min = plottable.length
    ? Math.min(decadeOf(Math.min(...plottable)), shortestSpanFloor)
    : shortestSpanFloor;

  return { min, max, ticks: buildLogTicks(min, max) };
};

export const yLogPct = (value: number, scale: IChartLaneLogScale): number => {
  const clamped = Math.min(scale.max, Math.max(scale.min, value));
  const span = Math.log10(scale.max) - Math.log10(scale.min);
  const fraction = (Math.log10(scale.max) - Math.log10(clamped)) / span;
  return CHART_LANE_Y_PADDING_PCT + fraction * PLOT_HEIGHT_PCT;
};

const POLYLINE_COORD_DECIMALS = 2;

export const toChartLanePolylinePoints = <T>(
  points: T[],
  xOf: (point: T) => number,
  yOf: (point: T) => number,
): string => {
  const coordinates = points.map((point) => {
    const x = xOf(point).toFixed(POLYLINE_COORD_DECIMALS);
    const y = yOf(point).toFixed(POLYLINE_COORD_DECIMALS);
    return `${x},${y}`;
  });

  return coordinates.join(' ');
};

export const buildChartLaneBiomaterialIndex = (
  biomaterials: IBiomaterial[],
): Map<number, IBiomaterial> => {
  const byId = new Map<number, IBiomaterial>();
  biomaterials.forEach((biomaterial) => byId.set(biomaterial.id, biomaterial));
  return byId;
};

export const resolveChartLanePointFields = (
  biosample: IBiosample,
  biomaterial?: IBiomaterial,
): IChartLanePointFields | null => {
  const collectionDate = getValidDate(biomaterial?.collectionDate);
  if (!collectionDate) {
    return null;
  }

  return {
    date: collectionDate.format('YYYY-MM-DD'),
    analyte: biosample.specimen || UNKNOWN_ANALYTE,
    assay: biomaterial?.downstreamProcess || UNKNOWN_ASSAY,
    manifestId: biosample.manifestId ?? UNKNOWN_MANIFEST_ID,
  };
};

export const isWithinChartLaneView = (xPct: number): boolean => (
  xPct >= -TIMELINE_VIEW_MARGIN_PCT && xPct <= 100 + TIMELINE_VIEW_MARGIN_PCT
);

export const formatChartLaneTick = (tick: number, scale: IChartLaneLogScale): string => (
  tick === scale.min ? '0' : `${tick}%`
);

const LABEL_X_MATCH_DECIMALS = 2;

export const buildLabelPositions = <T extends IChartLabel>(
  labels: T[],
): Array<T & { shiftPx: number }> => {
  const { minGapPct, shiftPx } = CHART_LANE_LABEL;
  const indexesBySide = new Map<string, number[]>();
  labels.forEach((label, index) => {
    const side = `${label.x.toFixed(LABEL_X_MATCH_DECIMALS)}|${label.placement}`;
    indexesBySide.set(side, [...(indexesBySide.get(side) ?? []), index]);
  });

  const shifts = labels.map(() => 0);

  indexesBySide.forEach((indexes) => {
    const topDown = [...indexes].sort((a, b) => (
      labels[a].y - labels[b].y || labels[a].key.localeCompare(labels[b].key)
    ));

    let clusterStart = 0;
    topDown.forEach((index, position) => {
      const previous = topDown[position - 1];
      const startsNewCluster = previous === undefined
        || labels[index].y - labels[previous].y >= minGapPct;
      if (startsNewCluster) {
        clusterStart = position;
      }
      const withinCluster = position - clusterStart;
      if (withinCluster === 0) {
        return;
      }
      const inwards = labels[index].x > LANE_MIDPOINT_PCT ? -1 : 1;
      const alternating = withinCluster % 2 === 1 ? 1 : -1;
      shifts[index] = Math.ceil(withinCluster / 2) * inwards * alternating * shiftPx;
    });
  });

  return labels.map((label, index) => ({ ...label, shiftPx: shifts[index] }));
};
